import { isValidGtin } from './domain.js';

// Browser-only XLSX reader using ZIP + standard browser APIs; no remote parser and no upload.
const utf8 = new TextDecoder();
const fail = (message) => { throw new Error(message); };
const sig = (view, offset) => view.getUint32(offset, true);
const MAX_EXCEL_BYTES = 10 * 1024 * 1024;
const MAX_XML_BYTES = 32 * 1024 * 1024;
function zipIndex(buffer) {
  const view = new DataView(buffer);
  let end = -1;
  for (let p = buffer.byteLength - 22; p >= Math.max(0, buffer.byteLength - 65557); p--) {
    if (sig(view, p) === 0x06054b50) { end = p; break; }
  }
  if (end < 0) fail('Arquivo Excel inválido (diretório ZIP não encontrado).');
  const count = view.getUint16(end + 10, true);
  const dir = view.getUint32(end + 16, true);
  if (count > 500 || dir >= buffer.byteLength) fail('Arquivo Excel acima dos limites de segurança.');
  const entries = new Map();
  let p = dir;
  for (let i = 0; i < count; i++) {
    if (p + 46 > buffer.byteLength || sig(view, p) !== 0x02014b50) fail('Estrutura ZIP inválida.');
    const method = view.getUint16(p + 10, true);
    const compressed = view.getUint32(p + 20, true);
    const expanded = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    if (expanded > MAX_XML_BYTES || compressed > MAX_EXCEL_BYTES) fail('Planilha grande demais para análise segura.');
    if (p + 46 + nameLen > buffer.byteLength) fail('Nome de arquivo ZIP inválido.');
    const name = utf8.decode(new Uint8Array(buffer, p + 46, nameLen));
    if (name.startsWith('xl/') && !name.includes('..')) entries.set(name, { method, compressed, expanded, local });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}
async function readEntry(buffer, entry) {
  if (!entry) return '';
  const view = new DataView(buffer);
  const { local, compressed, method, expanded } = entry;
  if (local + 30 > buffer.byteLength || sig(view, local) !== 0x04034b50) fail('Cabeçalho ZIP inválido.');
  const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
  if (start + compressed > buffer.byteLength) fail('ZIP truncado.');
  const bytes = new Uint8Array(buffer, start, compressed);
  let output;
  if (method === 0) output = bytes;
  else if (method === 8 && typeof DecompressionStream !== 'undefined') {
    output = new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))).arrayBuffer());
  } else fail('Este navegador não suporta descompactar este Excel. Use CSV UTF-8.');
  if (output.byteLength > MAX_XML_BYTES || (expanded && output.byteLength !== expanded)) fail('Conteúdo ZIP inconsistente.');
  return utf8.decode(output);
}
function xml(text) {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) fail('XML da planilha inválido.');
  return doc;
}
function first(parent, tag) { return parent.getElementsByTagName(tag)[0]; }
export async function readCatalogFile(file) {
  if (!file || file.size > MAX_EXCEL_BYTES) fail('Use um arquivo de até 10 MB.');
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.csv')) return csvRows(await file.text());
  if (!name.endsWith('.xlsx')) fail('Envie um arquivo .xlsx ou .csv.');
  const buffer = await file.arrayBuffer();
  const entries = zipIndex(buffer);
  const sheetPath = [...entries.keys()].filter((entry) => /^xl\/worksheets\/sheet[0-9]+\.xml$/.test(entry)).sort()[0];
  if (!sheetPath) fail('Nenhuma aba de dados encontrada.');
  const sharedText = await readEntry(buffer, entries.get('xl/sharedStrings.xml'));
  const shared = sharedText ? [...xml(sharedText).getElementsByTagName('si')].map(
    (si) => [...si.getElementsByTagName('t')].map((t) => t.textContent || '').join(''),
  ) : [];
  const document = xml(await readEntry(buffer, entries.get(sheetPath)));
  const data = [];
  for (const row of document.getElementsByTagName('row')) {
    const values = [];
    for (const cell of row.getElementsByTagName('c')) {
      const ref = String(cell.getAttribute('r') || '');
      const letters = (ref.match(/^[A-Z]+/) || [''])[0];
      if (!letters) continue;
      let index = 0;
      for (const letter of letters) index = index * 26 + letter.charCodeAt(0) - 64;
      if (index > 50) continue;
      const type = cell.getAttribute('t');
      const raw = type === 'inlineStr' ? (first(cell, 'is')?.textContent || '') : (first(cell, 'v')?.textContent || '');
      values[index - 1] = type === 's' ? (shared[Number(raw)] ?? '') : type === 'n' || (!type && raw !== '') ? Number(raw) : raw;
    }
    if (values.some((v) => v !== undefined && v !== '')) data.push(values);
    if (data.length > 30001) fail('Limite de 30 mil linhas por arquivo.');
  }
  return data;
}
export function csvRows(input) {
  const text = String(input || '').replace(/^\uFEFF/, '');
  const firstLine = text.slice(0, text.indexOf('\n') < 0 ? text.length : text.indexOf('\n'));
  const separator = firstLine.split(';').length > firstLine.split(',').length ? ';' : ',';
  const rows = [];
  let row = [], value = '', quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (quoted && text[i + 1] === '"') { value += '"'; i++; } else quoted = !quoted;
    } else if (ch === separator && !quoted) { row.push(value); value = ''; }
    else if ((ch === '\n' || ch === '\r') && !quoted) {
      if (ch === '\r' && text[i+1] === '\n') i++;
      row.push(value); if (row.some((x) => x !== '')) rows.push(row);
      row = []; value = '';
    } else value += ch;
  }
  if (quoted) fail('CSV com aspas não fechadas.');
  row.push(value); if (row.some((x) => x !== '')) rows.push(row);
  if (rows.length > 30001) fail('Limite de 30 mil linhas por arquivo.');
  return rows;
}
const heading = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const names = {
  sku: ['codigo','cod','sku','codigo interno','referencia'],
  name: ['descricao','produto','nome','nome produto'],
  cost_cents: ['preco de custo','custo','custo unitario'],
  price_cents: ['preco de venda','preco venda','preco','valor de venda','unit price'],
  opening_qty: ['estoque','saldo','quantidade','quantidade em estoque','stock qty'],
  min_stock: ['estoque minimo','minimo','saldo minimo'],
  category: ['categoria','grupo','departamento'],
  tax_code_ncm: ['ncm','codigo ncm'],
  tax_code_cest: ['cest','codigo cest'],
  barcode: ['codigo de barras','cod barras','ean','gtin','barcode'],
  unit: ['unidade','un','unit']
};
const getKey = (label) => Object.keys(names).find((k) => names[k].includes(heading(label)));
function num(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const text = String(v ?? '').trim().replace(/[R$\s]/g, '');
  if (text === '') return NaN;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  return Number(normalized);
}
function fiscal(value, digits) {
  const s = String(value ?? '').replace(/\D/g, '');
  return s.length === digits ? s : '';
}
export function reviewCatalog(rawRows, existing = [], filename = '') {
  if (!Array.isArray(rawRows) || rawRows.length < 2) fail('Planilha vazia ou sem cabeçalho.');
  const columns = new Map();
  rawRows[0].forEach((label, idx) => { const key = getKey(label); if (key && !columns.has(key)) columns.set(key, idx); });
  const missing = ['sku','name','price_cents'].filter((field) => !columns.has(field));
  if (missing.length) fail('Colunas obrigatórias ausentes: ' + missing.join(', '));
  const field = (row, key) => columns.has(key) ? row[columns.get(key)] : '';
  const rows = rawRows.slice(1).map((row, index) => {
    const sku = String(field(row,'sku') ?? '').trim().toUpperCase();
    const name = String(field(row,'name') ?? '').trim();
    const rawBarcode = String(field(row,'barcode') ?? '').trim();
    const barcode = rawBarcode.replace(/[^0-9]/g, '');
    const price = num(field(row,'price_cents'));
    const costRaw = field(row,'cost_cents');
    const cost = costRaw === '' || costRaw == null ? 0 : num(costRaw);
    const stockRaw = field(row,'opening_qty');
    const stock = stockRaw === '' || stockRaw == null ? 0 : num(stockRaw);
    const minRaw = field(row,'min_stock');
    const min = minRaw === '' || minRaw == null ? 0 : num(minRaw);
    const ncmOriginal = String(field(row,'tax_code_ncm') ?? '');
    const cestOriginal = String(field(row,'tax_code_cest') ?? '');
    const ncm = fiscal(ncmOriginal, 8), cest = fiscal(cestOriginal, 7);
    const issues = [];
    if (sku.length < 2 || sku.length > 80) issues.push('SKU ausente ou inválido');
    if (name.length < 2 || name.length > 250) issues.push('Nome ausente ou inválido');
    if (!Number.isFinite(price) || price < 0 || Math.abs(price*100 - Math.round(price*100)) > 1e-6) issues.push('Preço inválido');
    if (!Number.isFinite(cost) || cost < 0) issues.push('Custo inválido');
    if (!Number.isFinite(stock) || stock < 0 || stock > 1000000 || !Number.isInteger(stock)) issues.push(stock < 0 ? 'Estoque negativo: conferir inventário' : 'Quantidade fracionada requer configuração de unidade antes de importar');
    if (!Number.isFinite(min) || min < 0) issues.push('Estoque mínimo inválido');
    if (rawBarcode && (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(rawBarcode) || !isValidGtin(rawBarcode))) issues.push('Código de barras não é GTIN válido');
    if (ncmOriginal && !ncm) issues.push('NCM necessita revisão fiscal');
    if (cestOriginal && !cest) issues.push('CEST necessita revisão fiscal');
    const payload = {
      sku, name, barcode: barcode && barcode === rawBarcode ? barcode : null,
      price_cents: Number.isFinite(price) ? Math.round(price * 100) : -1,
      cost_cents: Number.isFinite(cost) ? Math.round(cost * 100) : 0,
      opening_qty: stock, min_stock: min, unit: String(field(row,'unit') || 'UN').trim().toUpperCase() || 'UN',
      tax_code_ncm: ncm, tax_code_cest: cest,
      metadata: { legacy_import: { source_file: filename.slice(0,140), source_row: index+2,
        original_sku: String(field(row,'sku') ?? ''), original_barcode: rawBarcode,
        original_stock: stockRaw, original_category: String(field(row,'category') ?? '') } }
    };
    return { line:index+2, payload, issues };
  });
  const duplicate = (getValue, label) => {
    const groups = new Map();
    rows.forEach((item) => { const value = getValue(item.payload); if (value) groups.set(value, [...(groups.get(value)||[]), item]); });
    for (const [key, group] of groups) if (group.length > 1) group.forEach((item) => item.issues.push(label + ' repetido: ' + key));
  };
  duplicate((p)=>p.sku,'SKU');
  duplicate((p)=>p.barcode,'Código de barras');
  const skus = new Set(existing.map((p) => String(p.sku||'').trim().toUpperCase()));
  const barcodes = new Set(existing.map((p) => String(p.barcode||'').trim()).filter(Boolean));
  rows.forEach((item) => {
    if (skus.has(item.payload.sku)) item.issues.push('SKU já existe no SmartPDV');
    if (item.payload.barcode && barcodes.has(item.payload.barcode)) item.issues.push('Código de barras já existe no SmartPDV');
  });
  return {
    rows,
    ready: rows.filter((row)=>row.issues.length===0),
    blocked: rows.filter((row)=>row.issues.length>0),
    counts: {
      total:rows.length,
      ready:rows.filter((row)=>row.issues.length===0).length,
      blocked:rows.filter((row)=>row.issues.length>0).length,
      zero:rows.filter((row)=>row.payload.opening_qty===0).length,
      negative:rows.filter((row)=>row.payload.opening_qty<0).length,
    }
  };
}
export function splitCatalogBatches(rows, size = 30) {
  if (!Number.isInteger(size) || size < 1 || size > 40) fail('Lote fora do limite autorizado.');
  const result=[];
  for(let i=0;i<rows.length;i+=size) result.push(rows.slice(i,i+size).map((row)=>row.payload||row));
  return result;
}
